/**
 * EU Right of Withdrawal — modal popup and form submission.
 */

class EuWithdrawalFormBase extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {HTMLButtonElement | null} */
  #submitButton = null;

  /** @type {HTMLButtonElement | null} */
  #reviewButton = null;

  /** @type {HTMLElement | null} */
  #confirmStep = null;

  /** @type {HTMLElement | null} */
  #summary = null;

  /** @type {HTMLElement | null} */
  #fields = null;

  /** @type {HTMLButtonElement | null} */
  #successCloseButton = null;

  /** @type {object | null} */
  #pendingPayload = null;

  #isConfirmStep = false;
  #isSuccess = false;
  #isSubmitting = false;

  connectedCallback() {
    this.#form = this.querySelector('form');
    this.#submitButton = this.querySelector('[data-confirm-button]');
    this.#reviewButton = this.querySelector('[data-review-button]');
    this.#confirmStep = this.querySelector('[data-confirmation-step]');
    this.#summary = this.querySelector('[data-review-summary]');
    this.#fields = this.querySelector('[data-form-fields]');
    this.#successCloseButton = this.querySelector('[data-success-close-button]');

    this.#form?.addEventListener('submit', this.#handleSubmit);
    this.#form?.addEventListener('input', this.#handleFieldInput);
    this.#form?.addEventListener('invalid', this.#handleInvalidField, true);
    this.#reviewButton?.addEventListener('click', this.#handleReviewStep);
    this.#submitButton?.addEventListener('click', this.#handleConfirmClick);
    this.#successCloseButton?.addEventListener('click', this.#handleSuccessClose);
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('submit', this.#handleSubmit);
    this.#form?.removeEventListener('input', this.#handleFieldInput);
    this.#form?.removeEventListener('invalid', this.#handleInvalidField, true);
    this.#reviewButton?.removeEventListener('click', this.#handleReviewStep);
    this.#submitButton?.removeEventListener('click', this.#handleConfirmClick);
    this.#successCloseButton?.removeEventListener('click', this.#handleSuccessClose);
  }

  #handleReviewStep = () => {
    if (!this.#form || this.#isSubmitting || this.#isSuccess) return;

    this.#hideStatuses();

    if (!this.#form.reportValidity()) {
      const firstInvalid = this.#form.querySelector(':invalid');
      if (firstInvalid instanceof HTMLElement) firstInvalid.focus();
      return;
    }

    const payload = this.#buildPayload();
    if (!payload) {
      this.#showError('Please complete all required fields and try again.');
      return;
    }

    this.#pendingPayload = payload;
    this.#buildSummary(payload);
    this.#setConfirmStep(true);
  };

  #handleConfirmClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!this.#isConfirmStep || this.#isSubmitting || this.#isSuccess) return;

    this.#submitWithdrawal();
  };

  #handleSuccessClose = () => {
    this.closest('eu-withdrawal-modal')?.close();
  };

  #handleSubmit = (event) => {
    event.preventDefault();
    if (this.#isConfirmStep) {
      this.#submitWithdrawal();
    }
  };

  #submitWithdrawal = async () => {
    if (!this.#form || this.#isSubmitting || this.#isSuccess || !this.#isConfirmStep) return;

    const endpoint = this.dataset.endpoint;
    if (!endpoint) {
      this.#showError('Form is not configured. Please contact the store.');
      return;
    }

    const honeypot = this.#form.querySelector('[name="website"]');
    if (honeypot instanceof HTMLInputElement && honeypot.value.trim() !== '') {
      return;
    }

    const payload = this.#pendingPayload || this.#buildPayload();
    if (!payload) {
      this.#showError('Please complete all required fields and try again.');
      this.#setConfirmStep(false);
      return;
    }

    this.#setLoading(true);
    this.#hideStatuses();

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Submission failed. Please try again.');
      }

      this.#showSuccess();
      this.#setSuccessState(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Submission failed. Please try again.';
      this.#showError(message);
    } finally {
      this.#setLoading(false);
    }
  };

  #getFieldValue(name) {
    const field = this.#form?.querySelector(`[name="${name}"]`);
    if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
      return field.value.trim();
    }
    return '';
  }

  #buildPayload() {
    if (!this.#form) return null;

    const statementField = this.#form.querySelector('[data-withdrawal-statement]');
    const withdrawalStatement =
      statementField instanceof HTMLInputElement
        ? statementField.value.trim() || statementField.defaultValue.trim()
        : this.#getFieldValue('withdrawal_statement');

    const payload = {
      full_name: this.#getFieldValue('full_name'),
      email: this.#getFieldValue('email'),
      order_number: this.#getFieldValue('order_number'),
      products: this.#getFieldValue('products'),
      withdrawal_statement: withdrawalStatement,
      country_code: this.dataset.country || '',
      store_name: this.dataset.storeName || '',
      page_url: window.location.href,
    };

    if (!payload.full_name || !payload.email || !payload.order_number || !payload.products || !payload.withdrawal_statement) {
      return null;
    }

    return payload;
  }

  #handleFieldInput = (event) => {
    if (!(event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement)) return;
    event.target.classList.remove('is-invalid');
    if (this.#isConfirmStep && !this.#isSuccess) {
      this.#pendingPayload = null;
      this.#setConfirmStep(false);
    }
  };

  #handleInvalidField = (event) => {
    if (!(event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement)) return;
    event.target.classList.add('is-invalid');
  };

  resetForm() {
    if (!this.#form) return;

    this.#isSubmitting = false;
    this.#isSuccess = false;
    this.#isConfirmStep = false;
    this.#pendingPayload = null;

    this.#form.hidden = false;
    this.#form.reset();

    const statementInput = this.#form.querySelector('[data-withdrawal-statement]');
    if (statementInput instanceof HTMLInputElement) {
      statementInput.value = statementInput.defaultValue;
    }

    const statementCheckbox = this.#form.querySelector('.eu-withdrawal__checkbox');
    if (statementCheckbox instanceof HTMLInputElement) {
      statementCheckbox.checked = true;
    }

    this.#hideStatuses();
    this.#setLoading(false);
    this.#restoreInitialUi();
  }

  #restoreInitialUi() {
    this.#fields?.removeAttribute('hidden');
    this.#confirmStep?.setAttribute('hidden', '');
    this.#reviewButton?.removeAttribute('hidden');
    this.#submitButton?.setAttribute('hidden', '');
    this.#successCloseButton?.setAttribute('hidden', '');

    if (this.#submitButton) {
      this.#submitButton.disabled = false;
    }

    if (this.#reviewButton) {
      this.#reviewButton.disabled = false;
    }

    if (this.#summary) {
      this.#summary.innerHTML = '';
    }
  }

  #setLoading(isLoading) {
    this.#isSubmitting = isLoading;

    if (this.#submitButton) {
      this.#submitButton.disabled = isLoading;
      const text = this.#submitButton.querySelector('.eu-withdrawal__submit-text');
      const loading = this.#submitButton.querySelector('.eu-withdrawal__submit-loading');
      if (text instanceof HTMLElement) text.hidden = isLoading;
      if (loading instanceof HTMLElement) loading.hidden = !isLoading;
    }

    if (this.#reviewButton) {
      this.#reviewButton.disabled = isLoading;
    }
  }

  #hideStatuses() {
    this.querySelector('.eu-withdrawal__status--success')?.setAttribute('hidden', '');
    this.querySelector('.eu-withdrawal__status--error')?.setAttribute('hidden', '');
  }

  #setConfirmStep(isConfirmStep) {
    this.#isConfirmStep = isConfirmStep;

    if (this.#isSuccess) return;

    this.#fields?.toggleAttribute('hidden', isConfirmStep);
    this.#confirmStep?.toggleAttribute('hidden', !isConfirmStep);
    this.#reviewButton?.toggleAttribute('hidden', isConfirmStep);
    this.#submitButton?.toggleAttribute('hidden', !isConfirmStep);
    this.#successCloseButton?.setAttribute('hidden', '');
  }

  #buildSummary(payload) {
    if (!this.#summary) return;

    const rows = [
      ['Full name', payload.full_name],
      ['Email', payload.email],
      ['Order number', payload.order_number],
      ['Product(s)', payload.products],
    ];

    this.#summary.innerHTML = rows
      .map(
        ([label, value]) =>
          `<div class="eu-withdrawal__summary-row"><strong>${label}</strong><span>${this.#escapeHtml(value)}</span></div>`
      )
      .join('');
  }

  #escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  #setSuccessState(isSuccess) {
    this.#isSuccess = isSuccess;

    if (isSuccess) {
      this.#fields?.setAttribute('hidden', '');
      this.#confirmStep?.setAttribute('hidden', '');
      this.#reviewButton?.setAttribute('hidden', '');
      this.#submitButton?.setAttribute('hidden', '');
      this.#successCloseButton?.removeAttribute('hidden');
      return;
    }

    this.#successCloseButton?.setAttribute('hidden', '');
    this.#fields?.toggleAttribute('hidden', this.#isConfirmStep);
    this.#confirmStep?.toggleAttribute('hidden', !this.#isConfirmStep);
    this.#reviewButton?.toggleAttribute('hidden', this.#isConfirmStep);
    this.#submitButton?.toggleAttribute('hidden', !this.#isConfirmStep);
  }

  #showSuccess() {
    const el = this.querySelector('.eu-withdrawal__status--success');
    el?.removeAttribute('hidden');
    if (el instanceof HTMLElement) el.focus();
  }

  #showError(message) {
    const el = this.querySelector('.eu-withdrawal__status--error');
    const messageEl = this.querySelector('[data-error-message]');

    if (messageEl) messageEl.textContent = message;
    el?.removeAttribute('hidden');
    if (el instanceof HTMLElement) el.focus();
  }
}

class EuWithdrawalForm extends EuWithdrawalFormBase {}

class EuWithdrawalModal extends EuWithdrawalFormBase {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  /** @type {number} */
  #scrollY = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#dialog = this.querySelector('dialog');

    this.#dialog?.addEventListener('close', () => {
      this.#unlockScroll();
      this.resetForm();
    });

    this.#dialog?.addEventListener('click', (event) => {
      if (event.target === this.#dialog) {
        this.close();
      }
    });

    this.querySelector('[data-eu-withdrawal-close]')?.addEventListener('click', () => {
      this.close();
    });
  }

  open() {
    if (!this.#dialog || this.#dialog.open) return;

    this.resetForm();

    this.#scrollY = window.scrollY;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#scrollY}px`;

    this.#dialog.showModal();
    this.querySelector('.eu-withdrawal__input:not([disabled])')?.focus();
  }

  close() {
    this.#dialog?.close();
  }

  #unlockScroll() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, this.#scrollY);
  }
}

if (!customElements.get('eu-withdrawal-form')) {
  customElements.define('eu-withdrawal-form', EuWithdrawalForm);
}

if (!customElements.get('eu-withdrawal-modal')) {
  customElements.define('eu-withdrawal-modal', EuWithdrawalModal);
}

document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('[data-eu-withdrawal-open]') : null;
  if (!trigger) return;

  event.preventDefault();
  document.querySelector('eu-withdrawal-modal')?.open();
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;

  const modal = document.querySelector('eu-withdrawal-modal');
  if (modal?.querySelector('dialog[open]')) {
    modal.close();
  }
});
